import { useState, useCallback } from "react";
import axios from "axios";
import useUsers from "hooks/userManagement/useUsers";
import { useAuth } from "contexts/AuthContext";

import { PROFILE_SERVICE_BASE_URL, STORAGE_BASE_URL } from "../constants";

const PROFILE_BASE_URL = PROFILE_SERVICE_BASE_URL;

const IMAGE_EXTS = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".heic"];

function isImage(filename = "") {
  const lower = filename.toLowerCase();
  return IMAGE_EXTS.some((ext) => lower.endsWith(ext));
}

function guessContentType(filename = "") {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".heic")) return "image/heic";
  return "image/jpeg";
}

async function compressImageToTarget(file, opts = {}) {
  const {
    maxBytes = 500_000,
    maxWidth = 1920,
    maxHeight = 1920,
    initialQuality = 0.8,
    minQuality = 0.5,
    stepQuality = 0.1,
    scaleStep = 0.9,
    forceJpeg = true,
  } = opts;

  const getBlob = (canvas, quality) =>
    new Promise((resolve) => {
      canvas.toBlob(resolve, forceJpeg ? "image/jpeg" : file.type, quality);
    });

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      let width = img.width;
      let height = img.height;
      const aspect = width / height;

      if (width > maxWidth || height > maxHeight) {
        if (width > height) {
          width = maxWidth;
          height = Math.floor(width / aspect);
        } else {
          height = maxHeight;
          width = Math.floor(height * aspect);
        }
      }

      let quality = initialQuality;

      const draw = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        return canvas;
      };

      let canvas = draw();
      let blob = await getBlob(canvas, quality);
      let size = blob.size;

      while (
        size > maxBytes &&
        (quality > minQuality || width > 640 || height > 640)
      ) {
        if (quality > minQuality) {
          quality = Math.max(minQuality, quality - stepQuality);
        } else {
          width = Math.floor(width * scaleStep);
          height = Math.floor(height * scaleStep);
        }
        canvas = draw();
        blob = await getBlob(canvas, quality);
        size = blob.size;
      }

      if (size > file.size) {
        resolve({ blob: file, size: file.size, contentType: file.type });
      } else {
        resolve({
          blob,
          size,
          contentType: forceJpeg ? "image/jpeg" : file.type,
        });
      }
    };

    img.onerror = (e) => reject(e);
    img.src = URL.createObjectURL(file);
  });
}

export function useFileUpload() {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [meta, setMeta] = useState(null);
  const { getUserById } = useUsers();
  const { user } = useAuth();

  const token = sessionStorage.getItem("authToken");

  const createUploadIntent = useCallback(
    async (
      userId,
      filename,
      fileSize,
      purpose = "USER_PROFILE",
      fileType = "PHOTO"
    ) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (userId) {
        const user = await getUserById(userId);
        if (!user || !user.id) throw new Error("User not found for uploading");
      }

      const body = { filename, fileType, size: fileSize, purpose };
      if (userId) body.userId = userId;

      const response = await axios.post(
        `${STORAGE_BASE_URL}/api/v1/files/upload-intent`,
        body,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "O-CLIENT-NAME": "OMC",
          },
        }
      );

      return response.data;
    },
    [token, getUserById]
  );

  const uploadToPresignedUrl = useCallback(
    async (blob, uploadUrl, contentType) => {
      const res = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType || "application/octet-stream" },
        body: blob,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Presigned upload failed: ${res.status} ${text}`);
      }
      return true;
    },
    []
  );

  const getFileMetadata = useCallback(
    async (fileId) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (!fileId) throw new Error("fileId is required");
      try {
        const res = await axios.get(
          `${STORAGE_BASE_URL}/api/v1/files/${fileId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "O-CLIENT-NAME": "OMC",
            },
          }
        );
        return res.data;
      } catch (err) {
        console.warn(
          "[useFileUpload] getFileMetadata failed for",
          fileId,
          err?.response?.status
        );
        throw err;
      }
    },
    [token]
  );

  const submitIdentityVerification = useCallback(
    async (userId, identificationDocumentType, idDocumentIds) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (!userId) throw new Error("userId is required");
      if (!identificationDocumentType)
        throw new Error("identificationDocumentType is required");
      if (
        !idDocumentIds ||
        !Array.isArray(idDocumentIds) ||
        idDocumentIds.length === 0
      )
        throw new Error("idDocumentIds must be a non-empty array");

      const body = {
        idDocumentIds,
        identificationDocumentType,
      };

      try {
        const res = await axios.post(
          `${PROFILE_BASE_URL}/api/v1/user-profiles/identity-verification/user/${userId}`,
          body,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "O-CLIENT-NAME": "OMC",
              "actor-id": user?.id || "",
            },
          }
        );
        return res.data;
      } catch (err) {
        console.error("[useFileUpload] submitIdentityVerification failed", err);
        throw err;
      }
    },
    [token, user?.id]
  );

  const submitWorkAuthorization = useCallback(
    async (userId, workAuthorizationType, idDocumentIds) => {
      if (!token) throw new Error("No auth token found in sessionStorage");
      if (!userId) throw new Error("userId is required");
      if (!workAuthorizationType)
        throw new Error("workAuthorizationType is required");
      if (
        !idDocumentIds ||
        !Array.isArray(idDocumentIds) ||
        idDocumentIds.length === 0
      )
        throw new Error("idDocumentIds must be a non-empty array");

      const body = {
        idDocumentIds,
        workAuthorizationType,
      };

      try {
        const res = await axios.post(
          `${PROFILE_BASE_URL}/api/v1/user-profiles/work-authorization/user/${userId}`,
          body,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "O-CLIENT-NAME": "OMC",
              "actor-id": user?.id || "",
            },
          }
        );
        return res.data;
      } catch (err) {
        console.error("[useFileUpload] submitWorkAuthorization failed", err);
        throw err;
      }
    },
    [token, user?.id]
  );

  const uploadFile = useCallback(
    async (userId, file, options = {}) => {
      console.log("[useFileUpload] ===== STARTING UPLOAD PROCESS =====");
      console.log("[useFileUpload] UserId:", userId);
      console.log("[useFileUpload] File details:", {
        name: file.name,
        size: file.size,
        type: file.type,
      });
      console.log("[useFileUpload] Options:", options);

      setUploading(true);
      setError(null);

      try {
        console.log("[useFileUpload] Starting upload for file:", file);

        let uploadBlob = file;
        let contentType = file.type || guessContentType(file.name);
        let fileSize = file.size;

        if (isImage(file.name)) {
          console.log("[useFileUpload] Compressing image...");
          const compressed = await compressImageToTarget(file, {
            forceJpeg: false,
          });
          uploadBlob = compressed.blob;
          fileSize = compressed.size;
          contentType = compressed.contentType;
          console.log("[useFileUpload] Image compressed, new size:", fileSize);
        }

        console.log("[useFileUpload] Creating upload intent...");
        const intent = await createUploadIntent(
          userId,
          file.name,
          fileSize,
          options.purpose
        );
        console.log("[useFileUpload] Upload intent response:", intent);

        if (!intent.uploadUrl) {
          throw new Error("No uploadUrl in upload intent response");
        }

        if (!intent.file?.id) {
          throw new Error("No file.id in upload intent response");
        }

        console.log("[useFileUpload] Uploading to presigned URL...");
        const uploadResult = await uploadToPresignedUrl(
          uploadBlob,
          intent.uploadUrl,
          contentType
        );
        console.log(
          "[useFileUpload] Upload to presigned URL result:",
          uploadResult
        );

        // Improved meta fallback
        const filenameFromFile = intent.file?.filename;
        const metaToSet = filenameFromFile
          ? { filename: filenameFromFile }
          : { filename: file.name };

        setFileId(intent.file.id);
        setMeta(metaToSet);
        console.log("[useFileUpload] intent.file full object:", intent.file);

        console.log("[useFileUpload] fileId set:", intent.file.id);
        console.log("[useFileUpload] meta set:", metaToSet);
        const returnValue = {
          fileId: intent.file.id,
          meta: metaToSet,
          file: intent.file || null,
        };
        console.log("[useFileUpload] Returning:", returnValue);
        console.log(
          "[useFileUpload] ===== UPLOAD PROCESS COMPLETED SUCCESSFULLY ====="
        );
        return returnValue;
      } catch (err) {
        console.error("[useFileUpload] ===== UPLOAD PROCESS FAILED =====");
        console.error("[useFileUpload] Upload failed:", err);
        setError(err.message);
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [createUploadIntent, uploadToPresignedUrl]
  );

  return {
    uploadFile,
    uploading,
    error,
    fileId,
    meta,
    getFileMetadata,
    submitIdentityVerification,
    submitWorkAuthorization,
  };
}
